import {Button, Group, Modal, Select, Stack, Switch, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import {useDispatchYards} from 'src/dispatch/use-dispatch'

import {
    type CreateUserInput,
    type UpdateUserInput,
    USER_ROLE_LABELS,
    type UserAccount,
    type UserRole,
    userRoleSchema,
} from './schemas'

type UserFormValues = {
    name: string
    email: string
    role: UserRole
    isSalesRep: boolean
    active: boolean
    dispatchYardId: string | null
}

const NO_YARD = 'none'

const roleOptions = userRoleSchema.options.map((role) => ({
    value: role,
    label: USER_ROLE_LABELS[role],
}))

type UserFormModalProps = {
    opened: boolean
    user?: UserAccount
    currentUserId?: string
    onClose: () => void
    onCreate: (input: CreateUserInput) => Promise<UserAccount>
    onUpdate: (userId: string, input: UpdateUserInput) => Promise<UserAccount>
    onSaved: () => void
}

export function UserFormModal({
    opened,
    user,
    currentUserId,
    onClose,
    onCreate,
    onUpdate,
    onSaved,
}: UserFormModalProps) {
    const isEdit = user != null
    const [isSubmitting, setIsSubmitting] = useState(false)
    const {data: yards = []} = useDispatchYards()
    const yardOptions = [
        {value: NO_YARD, label: 'No yard (both yards)'},
        ...yards.map((yard) => ({value: yard.id, label: yard.name})),
    ]

    const form = useForm<UserFormValues>({
        initialValues: {
            name: '',
            email: '',
            role: 'member',
            isSalesRep: false,
            active: true,
            dispatchYardId: null,
        },
        validate: {
            name: (value) => (value.trim() ? null : 'Name is required'),
            email: (value) => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Valid email required'),
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }

        form.setValues({
            name: user?.name ?? '',
            email: user?.email ?? '',
            role: user?.role ?? 'member',
            isSalesRep: user?.isSalesRep ?? false,
            active: user?.deactivatedAt == null,
            dispatchYardId: user?.dispatchYardId ?? null,
        })
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened, user])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: UserFormValues) {
        setIsSubmitting(true)
        try {
            if (isEdit && user) {
                await onUpdate(user.id, {
                    name: values.name.trim(),
                    role: values.role,
                    isSalesRep: values.isSalesRep,
                    active: values.active,
                    dispatchYardId: values.dispatchYardId,
                })
            } else {
                await onCreate({
                    name: values.name.trim(),
                    email: values.email.trim(),
                    role: values.role,
                    isSalesRep: values.isSalesRep,
                    dispatchYardId: values.dispatchYardId,
                })
            }
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    const editingSelf = isEdit && user.id === currentUserId

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isEdit ? 'Edit user' : 'Add user'}
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                    <TextInput
                        label="Email"
                        withAsterisk
                        disabled={isEdit}
                        {...form.getInputProps('email')}
                    />
                    <Select
                        label="Role"
                        withAsterisk
                        data={roleOptions}
                        disabled={editingSelf}
                        {...form.getInputProps('role')}
                    />
                    <Select
                        label="Yard"
                        description="Decides who publishes, the board they open on and which yard's work they can change"
                        data={yardOptions}
                        allowDeselect={false}
                        value={form.values.dispatchYardId ?? NO_YARD}
                        onChange={(value) =>
                            form.setFieldValue(
                                'dispatchYardId',
                                value == null || value === NO_YARD ? null : value,
                            )
                        }
                    />
                    <Switch
                        label="Sales rep"
                        description="Can edit only their own orders and jobs"
                        checked={form.values.isSalesRep}
                        onChange={(event) =>
                            form.setFieldValue('isSalesRep', event.currentTarget.checked)
                        }
                    />
                    {isEdit && (
                        <Switch
                            label="Active"
                            disabled={editingSelf}
                            checked={form.values.active}
                            onChange={(event) =>
                                form.setFieldValue('active', event.currentTarget.checked)
                            }
                        />
                    )}

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            {isEdit ? 'Save changes' : 'Add user'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
